import {
  VALID,
  isBlank,
  isValidDateOfService,
  isValidDatePrescribed,
  isValidRXNumber,
  isValidNDC,
  isValidQuantity,
  isQuestionableQuantity,
  isValidWholesalerInvoiceNumber,
  isValidPrescriberIDQualifier,
  isValidServiceProviderIDQualifier,
  isValidNPI,
  isValidNCPDP,
  isValidDEA,
  isValidIdentifier,
  isValidId340b,
  isValidPayerBin,
  isValidPCN,
  isValidFillNumber,
  isValidNonFutureDate,
  isValidAccount340b,
  isValidProductSerializationNumber,
} from "./DataValidations";
import { isRequiredField } from "../Schemas/PharmacyClaim";
import SubmissionTranslator from "../../../utils/SubmissionTranslator";

const translator = new SubmissionTranslator("pharmacy");

const isValidPharmacyClaimField = (value, column, row, mapping) => {
  if (["formatted_rx_number"].includes(column)) {
    return VALID;
  }

  const requiredResult = checkRequired(column, value);
  if (!requiredResult.isValid) {
    return requiredResult;
  }

  switch (column) {
    case "date_of_service":
      if (!isValidNonFutureDate(value)) {
        return {
          isValid: false,
          message: translator.getError("invalid_date"),
        };
      }

      if (!isValidDateOfService(value, row[mapping["date_prescribed"]])) {
        return {
          isValid: false,
          message: translator.getError("date_before_prescribed"),
        };
      }
      return VALID;

    case "date_prescribed":
      if (!isValidNonFutureDate(value)) {
        return {
          isValid: false,
          message: translator.getError("invalid_date"),
        };
      }

      if (!isValidDatePrescribed(value, row[mapping["date_of_service"]])) {
        return {
          isValid: false,
          message: translator.getError("date_before_prescribed"),
        };
      }
      return VALID;

    case "rx_number":
      if (!isValidRXNumber(value)) {
        return {
          isValid: false,
          message: translator.getError("invalid_rx_number"),
        };
      }
      return VALID;

    case "ndc":
      if (!isValidNDC(value)) {
        return {
          isValid: false,
          message: translator.getError("invalid_ndc"),
        };
      }
      return VALID;

    case "quantity":
      if (!isValidQuantity(value)) {
        return { isValid: false, message: translator.getError("invalid_quantity") };
      }
      if (isQuestionableQuantity(value)) {
        return { isValid: false, message: translator.getError("quantity_over_max") };
      }
      return VALID;

    case "wholesaler_invoice_number":
      if (!isValidWholesalerInvoiceNumber(value)) {
        return {
          isValid: false,
          message: translator.getError("invalid_wholesaler_invoice_number"),
        };
      }
      return VALID;

    case "prescriber_id_qualifier":
      if (!isValidPrescriberIDQualifier(value)) {
        return {
          isValid: false,
          message: translator.getError("invalid_prescriber_id_qualifier"),
        };
      }
      return VALID;

    case "prescriber_id":
      return validateIdentifierByQualifier(value, row[mapping["prescriber_id_qualifier"]]);

    case "service_provider_id_qualifier":
      if (!isValidServiceProviderIDQualifier(value)) {
        return {
          isValid: false,
          message: translator.getError("invalid_service_provider_id_qualifier"),
        };
      }
      return VALID;

    case "service_provider_id":
      return validateIdentifierByQualifier(value, row[mapping["service_provider_id_qualifier"]]);

    case "contracted_entity_id":
      if (!isValidId340b(value)) {
        return {
          isValid: false,
          message: translator.getError("invalid_id_340b"),
        };
      }
      return VALID;

    case "payer_bin":
      if (!isValidPayerBin(value)) {
        return { isValid: false, message: translator.getError("invalid_payer_bin") };
      }
      return VALID;

    case "payer_pcn":
      if (!isValidPCN(value)) {
        return { isValid: false, message: translator.getError("invalid_payer_pcn") };
      }
      return VALID;

    case "ship_to_location":
      if (!value) return VALID;
      if (!isValidIdentifier(value)) {
        return { isValid: false, message: translator.getError("invalid_identifier") };
      }
      return VALID;

    case "ship_to_date":
      if (!value) return VALID;
      if (!isValidNonFutureDate(value)) {
        return { isValid: false, message: translator.getError("invalid_date") };
      }
      return VALID;

    case "account_340b":
      if (!isValidAccount340b(value)) {
        return { isValid: false, message: translator.getError("invalid_alphanumeric_20") };
      }
      return VALID;

    case "product_serialization_number":
      if (!isValidProductSerializationNumber(value)) {
        return { isValid: false, message: translator.getError("invalid_alphanumeric_20") };
      }
      return VALID;

    case "fill_number":
      if (!isValidFillNumber(value)) {
        return { isValid: false, message: translator.getError("invalid_fill_number") };
      }
      return VALID;
  }

  return VALID;
};

const checkRequired = (column, value) => {
  if (isBlank(value) && isRequiredField(column)) {
    return { isValid: false, message: translator.getError("field_required", { column }) };
  }

  return VALID;
};

const validateIdentifierByQualifier = (val, qualifier) => {
  if (!val) return VALID;

  switch (qualifier) {
    case "1":
    case "01":
      if (!isValidNPI(val)) return { isValid: false, message: translator.getError("invalid_npi") };
      break;
    case "7":
    case "07":
      if (!isValidNCPDP(val))
        return { isValid: false, message: translator.getError("invalid_ncpdp") };
      break;
    case "12":
      if (!isValidDEA(val)) return { isValid: false, message: translator.getError("invalid_dea") };
      break;
    default:
      if (!isValidIdentifier(val))
        return { isValid: false, message: translator.getError("invalid_identifier") };
  }

  return VALID;
};

export default isValidPharmacyClaimField;
